// Per-account journey derivation for the admin Accounts tab (History view).
//
// Everything here is a pure function of data the Accounts tab already loads:
// the accountsInfo doc, the per-family firstSignin times, and the
// usageAnalytics docs written by the /admin/updateUsageAnalytics sweep.
// Nothing is stored — tweak the definitions here and the view updates on the
// next render.
//
// The order/ship/deliver spine comes straight off accountsInfo, so milestones
// render for every account regardless of whether the sweep has ever run.

import { toMs } from './usageTiers';

const DAY_MS = 24 * 3600 * 1000;

/**
 * Firestore Timestamp | epoch ms | {seconds} | ISO string -> epoch ms (or null).
 *
 * Looser than usageTiers' `toMs` because `prepaymentDate` arrives from a CSV
 * import: /admin/importAccount converts any field whose name contains "date"
 * to a Timestamp, but older records may still hold the raw ISO string.
 */
export function toHistoryMs(v) {
  const t = toMs(v);
  if (t != null) return t;
  if (typeof v === 'string') {
    const parsed = Date.parse(v);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
}

// Beyond this many days, an elapsed span reads better in months than in an
// ever-growing day count ("412d ago" tells you nothing at a glance).
export const ELAPSED_MONTHS_THRESHOLD_DAYS = 60;
const AVG_MONTH_DAYS = 30.44;

/**
 * How long ago something was, for the History view's date columns:
 * "today" / "12d ago" / "3.5mo ago".
 *
 * Whole days below ELAPSED_MONTHS_THRESHOLD_DAYS, then months to one decimal.
 * Deliberately coarser than `formatRelativeTime` — on a journey timeline the
 * hour something happened six months ago is noise.
 *
 * Returns null for a missing date, so callers render their own placeholder.
 */
export function formatElapsed(ms, nowMs = Date.now()) {
  if (ms == null) return null;
  const days = Math.floor((nowMs - ms) / DAY_MS);
  if (days < 0) return 'in the future';
  if (days === 0) return 'today';
  if (days < ELAPSED_MONTHS_THRESHOLD_DAYS) return `${days}d ago`;
  return `${(days / AVG_MONTH_DAYS).toFixed(1)}mo ago`;
}

/** "3 Jul 2026" — the date itself, for the column above the elapsed line. */
export function formatShortDate(ms) {
  if (ms == null) return null;
  return new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Milestones
// ---------------------------------------------------------------------------

// Rendering order is also legend order. `shape` is consumed by HistoryTimeline.
export const MILESTONE_DEFS = [
  { key: 'ordered', label: 'Ordered', color: '#7c3aed', shape: 'diamond' },
  { key: 'shipped', label: 'Shipped', color: '#2563eb', shape: 'triangle' },
  { key: 'delivered', label: 'Delivered', color: '#0891b2', shape: 'circle' },
  { key: 'setup', label: 'Set up', color: '#16a34a', shape: 'star' },
  { key: 'firstSignin', label: 'First sign-in', color: '#65a30d', shape: 'square' },
  { key: 'firstSession', label: 'First use', color: '#ca8a04', shape: 'circle' },
  { key: 'firstCall', label: 'First call', color: '#15803d', shape: 'diamond' },
  { key: 'subSetUp', label: 'Subscription set up', color: '#db2777', shape: 'square' },
  { key: 'subStarted', label: 'Billing started', color: '#be123c', shape: 'triangle' },
  { key: 'cancelRequested', label: 'Cancel requested', color: '#f97316', shape: 'cross' },
  { key: 'cancelled', label: 'Cancelled', color: '#6b7280', shape: 'cross' },
];

const MILESTONE_BY_KEY = Object.fromEntries(MILESTONE_DEFS.map(d => [d.key, d]));

/** Smallest non-null value produced by `pick` over the account's families. */
function minAcrossFamilies(account, pick) {
  let best = null;
  for (const fid of account?.families || []) {
    const t = pick(fid);
    if (t != null && (best === null || t < best)) best = t;
  }
  return best;
}

/**
 * Journey milestones for one account, oldest first. Milestones with no
 * recorded date are omitted entirely — never guessed, never zero-filled.
 *
 * account          — accountsInfo doc ({ id, families: [...], ... })
 * firstSigninTimes — { [familyId]: epoch ms } already loaded by the tab
 * usageAnalytics   — { [familyId]: usageAnalytics doc }
 *
 * Returns [{ key, label, color, shape, ms, detail }].
 */
export function accountMilestones(account, firstSigninTimes = {}, usageAnalytics = {}) {
  const a = account || {};
  const out = [];
  const add = (key, ms, detail) => {
    if (ms == null) return;
    out.push({ ...MILESTONE_BY_KEY[key], ms, detail: detail || null });
  };

  add('ordered', toHistoryMs(a.prepaymentDate));
  add('shipped', toHistoryMs(a.shippedAt));

  // Delivery is per tracking number. The milestone is the FIRST unit to
  // arrive; the detail says how many of them have landed, because a split
  // delivery can be half-complete for weeks.
  const deliveryMs = (a.deliveryDates || []).map(toHistoryMs).filter(t => t != null);
  if (deliveryMs.length > 0) {
    const total = (a.trackingNumbers || []).length || deliveryMs.length;
    add(
      'delivered',
      Math.min(...deliveryMs),
      total > 1 ? `${deliveryMs.length} of ${total} units delivered` : null
    );
  }

  add('setup', toHistoryMs(a.setupCompletedAt));
  add('firstSignin', minAcrossFamilies(a, fid => firstSigninTimes?.[fid] ?? null));
  add('firstSession', minAcrossFamilies(a, fid => toHistoryMs(usageAnalytics?.[fid]?.firstSessionAt)));
  add('firstCall', minAcrossFamilies(a, fid => toHistoryMs(usageAnalytics?.[fid]?.firstMultiPersonCallAt)));

  add('subSetUp', toHistoryMs(a.subscriptionActivatedAt), a.subscriptionActivatedByEmail || null);
  add('subStarted', toHistoryMs(a.subscriptionStartDate), a.subscriptionActivatedTier || null);
  add('cancelRequested', toHistoryMs(a.subscriptionCancelRequestedAt));
  add('cancelled', toHistoryMs(a.subscriptionCanceledAt), a.subscriptionCanceledReason || null);

  out.sort((x, y) => x.ms - y.ms);
  return out;
}

// ---------------------------------------------------------------------------
// Daily device state
// ---------------------------------------------------------------------------

// dailyStates day-record keys, as written by the cloud sweep.
const DAY_KEY_STATE = { i: 'igloo', h: 'home', a: 'asleep', o: 'offline', u: 'unknown' };

// A day needs at least this many minutes of igloo time to read as an igloo
// day. Without a floor, a device that drifts into a Higlo for two minutes a
// day paints solid green and is indistinguishable from a heavy user — which
// defeats the point of a view built to judge engagement at a glance. With it,
// a genuine call (the sweep's own MIN_CALL_MS floor is 1 minute of overlap,
// and real calls run to tens of minutes) still colours the day.
export const IGLOO_DAY_MIN_MINUTES = 5;

/**
 * The state a day should be drawn as.
 *
 * A day with a meaningful amount of igloo time reads as an igloo day even when
 * some other state has more minutes: at multi-month zoom a day is a pixel or
 * two, and "they were in a Higlo that day" is the most informative thing the
 * strip can say — the same reasoning that makes the Usage view draw igloo
 * slivers on top of their neighbours. Below that floor, and for every other
 * case, the state with the most minutes wins.
 *
 * Returns 'igloo' | 'home' | 'asleep' | 'offline' | 'unknown' | null.
 */
export function dominantState(day) {
  if (!day || typeof day !== 'object') return null;
  if (typeof day.i === 'number' && day.i >= IGLOO_DAY_MIN_MINUTES) return 'igloo';
  let bestKey = null;
  let bestVal = -1;
  for (const [k, v] of Object.entries(day)) {
    if (!DAY_KEY_STATE[k] || typeof v !== 'number') continue;
    // Igloo wins a tie — it is the state worth seeing.
    if (v > bestVal || (v === bestVal && k === 'i')) {
      bestVal = v;
      bestKey = k;
    }
  }
  return bestKey ? DAY_KEY_STATE[bestKey] : null;
}

/**
 * One daily-state record per family on the account.
 *
 * `dailyAnalytics` is the usageAnalyticsDaily collection keyed by family id —
 * a SEPARATE collection from usageAnalytics, loaded only while the History
 * view is on screen (see useAccountDailyStates).
 *
 * `days` is null when the sweep has never computed this family, which the
 * renderer must show as "not computed" rather than as an offline device.
 * `fromMs`/`throughMs` bound what HAS been computed, so a partially
 * backfilled family is drawn honestly.
 */
export function accountDailyStates(account, dailyAnalytics = {}) {
  return (account?.families || []).map((fid) => {
    const a = dailyAnalytics?.[fid] || {};
    const days = a.dailyStates && typeof a.dailyStates === 'object' ? a.dailyStates : null;
    return {
      familyId: fid,
      days,
      fromMs: typeof a.dailyFromMs === 'number' ? a.dailyFromMs : null,
      throughMs: typeof a.dailyThroughMs === 'number' ? a.dailyThroughMs : null,
      complete: !!a.dailyComplete,
    };
  });
}

/**
 * Collapse a family's computed days into runs of consecutive same-state days.
 *
 * The strip is drawn from these rather than one rect per day. A year-long
 * window across a fleet would otherwise emit tens of thousands of SVG nodes,
 * each with its own tooltip, rebuilt on every range click and re-sort; runs are
 * bounded by how often devices actually change state instead of by the window
 * length. The rendered result is identical, because adjacent same-state days
 * are the same colour.
 *
 * Returns [{ state, startMs, endMs, days }] oldest first, covering only days
 * inside BOTH the window and the family's computed range.
 */
export function dayRuns(daily, windowStartMs, windowEndMs) {
  const out = [];
  if (!daily?.days) return out;

  const first = utcDayStart(Math.max(windowStartMs, daily.fromMs ?? windowStartMs));
  const limit = Math.min(windowEndMs, daily.throughMs ?? windowEndMs);

  let run = null;
  for (let d = first; d < limit; d += DAY_MS) {
    const state = dominantState(daily.days[dayKey(d)]);
    // An uncomputed or all-unknown day breaks the run: it must stay the
    // neutral base, never be absorbed into a neighbouring colour.
    if (!state || state === 'unknown') {
      run = null;
      continue;
    }
    if (run && run.state === state && run.endMs === d) {
      run.endMs = d + DAY_MS;
      run.days += 1;
    } else {
      run = { state, startMs: d, endMs: d + DAY_MS, days: 1 };
      out.push(run);
    }
  }
  return out;
}

/** Epoch ms of the UTC midnight that starts a day — matches the cloud sweep. */
export function utcDayStart(ms) {
  return Math.floor(ms / DAY_MS) * DAY_MS;
}

/** UTC-midnight epoch ms -> the "YYYY-MM-DD" key used in dailyStates. */
export function dayKey(dayStartMs) {
  return new Date(dayStartMs).toISOString().slice(0, 10);
}

/** Earliest day any family of this account has computed state for, or null. */
export function earliestComputedDayMs(dailyStates) {
  let best = null;
  for (const fs of dailyStates || []) {
    if (fs.fromMs != null && (best === null || fs.fromMs < best)) best = fs.fromMs;
  }
  return best;
}

// ---------------------------------------------------------------------------
// The shared time window
// ---------------------------------------------------------------------------

export const HISTORY_RANGES = [
  { key: '3m', label: '3 mo', months: 3 },
  { key: '6m', label: '6 mo', months: 6 },
  { key: '12m', label: '12 mo', months: 12 },
  { key: 'all', label: 'All', months: null },
];

export const DEFAULT_HISTORY_RANGE = { preset: '6m' };

// The "All" preset never reaches further back than this, however malformed a
// stored date turns out to be. Mirrors ABSOLUTE_FLOOR_MS in the cloud sweep —
// the product did not exist before it.
export const HISTORY_FLOOR_MS = Date.UTC(2025, 0, 1);

/** "YYYY-MM" -> epoch ms of the first instant of that month (UTC). */
export function monthStartMs(monthStr) {
  if (typeof monthStr !== 'string') return null;
  const m = /^(\d{4})-(\d{2})$/.exec(monthStr.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (month < 1 || month > 12) return null;
  return Date.UTC(year, month - 1, 1);
}

/** "YYYY-MM" -> epoch ms of the first instant of the FOLLOWING month (UTC). */
export function monthEndMs(monthStr) {
  const start = monthStartMs(monthStr);
  if (start == null) return null;
  const d = new Date(start);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
}

/** Epoch ms -> the "YYYY-MM" its month is identified by. */
export function toMonthStr(ms) {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * Is a custom range usable? False when either month is missing or malformed,
 * or the pair is inverted — which is easy to reach by setting "to" first and
 * then picking a later "from". Exported so the controls can say so rather than
 * silently displaying a range the chart is not drawing.
 */
export function isCustomRangeValid(range) {
  if (range?.preset !== 'custom') return true;
  const start = monthStartMs(range?.fromMonth);
  const end = monthEndMs(range?.toMonth);
  return start != null && end != null && end > start;
}

/**
 * Resolve the range control into the ONE window every row is drawn against.
 * Rows sharing an axis is the whole point of this view, so this is computed
 * once for the table, never per row.
 *
 * range      — { preset: '3m'|'6m'|'12m'|'all' } or
 *              { preset: 'custom', fromMonth: 'YYYY-MM', toMonth: 'YYYY-MM' }
 * earliestMs — oldest known event across the DISPLAYED accounts, for 'all'
 * nowMs      — injectable clock
 *
 * Always returns a window with startMs < endMs.
 */
export function resolveHistoryWindow(range, earliestMs, nowMs = Date.now()) {
  const preset = range?.preset || DEFAULT_HISTORY_RANGE.preset;

  if (preset === 'custom') {
    const start = monthStartMs(range?.fromMonth);
    const end = monthEndMs(range?.toMonth);
    if (start != null && end != null && end > start) {
      // Never draw into the future — an axis running past today wastes half
      // the width and makes rows look dead.
      return { startMs: start, endMs: Math.min(end, nowMs) > start ? Math.min(end, nowMs) : end };
    }
    // Fall through to the default preset on an incomplete/invalid custom range.
  }

  if (preset === 'all') {
    const anchor = earliestMs != null && earliestMs < nowMs
      ? monthStartMs(toMonthStr(earliestMs))
      : null;
    // Floor it. `earliestMs` is derived from stored dates — including
    // `prepaymentDate`, which /admin/importAccount coerces from whatever a CSV
    // held — and every row shares this axis, so one absurd date anywhere in the
    // fleet would set the window for all of them. A 1970 anchor means ~670
    // month gridlines per row and tens of thousands of SVG nodes in one
    // synchronous render. The sweep floors its own equivalent input for the
    // same reason (ABSOLUTE_FLOOR_MS in usageAnalytics.js).
    const start = anchor != null ? Math.max(anchor, HISTORY_FLOOR_MS) : null;
    // With nothing to anchor to, 12 months is a more useful blank canvas than
    // an arbitrarily long empty axis.
    return { startMs: start ?? addMonths(nowMs, -12), endMs: nowMs };
  }

  const def = HISTORY_RANGES.find(r => r.key === preset) || HISTORY_RANGES[1];
  const months = def.months ?? 6;
  return { startMs: addMonths(nowMs, -months), endMs: nowMs };
}

/** Shift by whole calendar months in UTC (clamping short months). */
export function addMonths(ms, delta) {
  const d = new Date(ms);
  const targetMonth = d.getUTCMonth() + delta;
  const candidate = Date.UTC(
    d.getUTCFullYear(),
    targetMonth,
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds()
  );
  // Date.UTC rolls a too-long day over into the next month (31 Jan -> 3 Mar);
  // clamp back to the last day of the intended month instead.
  const rolled = new Date(candidate);
  const intended = ((targetMonth % 12) + 12) % 12;
  if (rolled.getUTCMonth() !== intended) {
    return Date.UTC(d.getUTCFullYear(), targetMonth + 1, 0, d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds());
  }
  return candidate;
}

/**
 * Oldest thing worth showing for one account — the earliest milestone or
 * computed day. Feeds the 'all' preset so the axis starts where the data does.
 */
export function accountEarliestMs(milestones, dailyStates, calls) {
  let best = null;
  const consider = (t) => {
    if (t != null && (best === null || t < best)) best = t;
  };
  for (const m of milestones || []) consider(m.ms);
  consider(earliestComputedDayMs(dailyStates));
  // Calls are newest-first (see accountCalls), so the last entry is the oldest.
  if (calls && calls.length > 0) consider(calls[calls.length - 1].startMs);
  return best;
}

// ---------------------------------------------------------------------------
// Month gridlines — shared by the axis header and every row
// ---------------------------------------------------------------------------

/**
 * Month boundaries inside a window, as { ms, label } oldest first.
 * The label is short ("Feb"), gaining a year ("Jan 26") each January and on
 * the first tick, so a 12-month axis is never ambiguous.
 */
export function monthTicks(startMs, endMs) {
  const out = [];
  if (!(endMs > startMs)) return out;
  const first = new Date(startMs);
  let cursor = Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 1);
  while (cursor <= endMs) {
    const d = new Date(cursor);
    const month = d.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' });
    const showYear = d.getUTCMonth() === 0 || out.length === 0;
    out.push({
      ms: cursor,
      label: showYear ? `${month} ${String(d.getUTCFullYear()).slice(2)}` : month,
    });
    cursor = Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
  }
  return out;
}
